import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, CreditCard, AlertCircle, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import PageHeader from '../common/PageHeader';
import Footer from '../common/Footer';

const Terms = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      {/* Content */}
      <div className="container max-w-4xl py-16">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="h-10 w-10 text-primary" />
          <div>
            <h1 className="text-4xl font-bold">Allmänna villkor</h1>
            <p className="text-muted-foreground mt-2">Uppdaterad: {new Date().toLocaleDateString('sv-SE')}</p>
          </div>
        </div>

        <Alert className="mb-8">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription className="text-base">
            Genom att använda Dekalio godkänner du följande villkor.
          </AlertDescription>
        </Alert>

        <div className="space-y-8">
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Tjänstens omfattning</h2>
            </div>
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">Dekalio tillhandahåller:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Ett digitalt designverktyg</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Automatiska designkontroller</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Professionell granskning innan produktion</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Design & ansvar</h2>
            </div>
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Ditt ansvar</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Du äger ansvaret för innehåll, texter och varumärkesrättigheter.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Vårt ansvar</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Vi ansvarar för att designen är tekniskt möjlig att producera enligt våra riktlinjer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Vi garanterar inte marknadsföringsresultat eller juridisk korrekthet i innehållet</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Betalning</h2>
            </div>
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="mt-1">1</Badge>
                    <div>
                      <p className="font-medium mb-1">Design sker utan kostnad</p>
                      <p className="text-sm text-muted-foreground">
                        Du kan designa helt gratis och se exakt pris innan du bestämmer dig
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="mt-1">2</Badge>
                    <div>
                      <p className="font-medium mb-1">Betalning sker först efter godkänd design och fastställt pris</p>
                      <p className="text-sm text-muted-foreground">
                        När du godkänt korrekturet och priset genomför du betalningen
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <Separator />

          <section>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-semibold">Avbeställning & reklamation</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Avbeställning</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Ingen betalning = inget köp. Du kan avbryta när som helst innan du betalar.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Reklamation</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Reklamation gäller endast produktion som avviker från godkänd design.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Alert className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Viktigt att veta</AlertTitle>
              <AlertDescription>
                Eftersom varje design är unik och produceras efter godkännande, kan vi inte erbjuda ångerrätt 
                efter att produktionen påbörjats. Du får alltid se och godkänna ett digitalt korrektur först.
              </AlertDescription>
            </Alert>
          </section>

          <div className="flex gap-4 pt-8">
            <Button onClick={() => navigate('/')}>
              Tillbaka till startsidan
            </Button>
            <Button variant="outline" onClick={() => navigate('/integritetspolicy')}>
              Läs integritetspolicyn
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;

